import { useCallback, useEffect, useMemo, useState } from 'react';
import { db } from '@/lib/db';
import type { CourseHole, GroupRoundEnriched, Member } from '@/lib/db/types';
import {
  buildRoundPlayers,
  computeAllGames,
  aggregateNetOwed,
  minimizePayments,
  scoresMatrixFromDb,
} from '@/lib/games/engine/compute-round';
import { strokePlugin } from '@/lib/games/engine/plugins/stroke';
import type { GameResult, StakesConfig, WolfRoundConfig, NassauMatchConfig } from '@/lib/games/engine/types';
import { defaultStakeForFormat, stakeToDbRow, getLegStakes } from '@/lib/games/engine/stakes-helpers';
import { defaultNassauMatches } from '@/lib/games/engine/nassau-matches';
import { findCatalogEntry } from '@/lib/games/game-catalog';
import { catalogRequiresTeams } from '@/lib/games/engine/registry';
import type { SimplifiedPayment } from '@/lib/db/types';

export function useRoundEngine(
  round: GroupRoundEnriched | null,
  member: Member,
  prefetchedHoles?: CourseHole[],
) {
  const holeOffset = round?.holes === 'back-9' ? 9 : 0;
  const holeCount = round?.holes === 'full' ? 18 : 9;
  const [holes, setHoles] = useState<CourseHole[]>(prefetchedHoles || []);
  const [scoresByKey, setScoresByKey] = useState<Record<string, (number | null)[]>>({});
  const [puttsByKey, setPuttsByKey] = useState<Record<string, (number | null)[]>>({});
  const [gameResults, setGameResults] = useState<GameResult[]>([]);
  const [netByPlayer, setNetByPlayer] = useState<Record<string, number>>({});
  const [payments, setPayments] = useState<SimplifiedPayment[]>([]);
  const [teams, setTeams] = useState<string[][] | undefined>();

  const players = round?.players || [];
  const games = round?.games || [];
  const enabledGames = useMemo(() => games.filter((g) => g.enabled), [games]);
  const isCasual = enabledGames.length === 0;

  useEffect(() => {
    if (prefetchedHoles?.length) {
      setHoles(prefetchedHoles);
      return;
    }
    if (!round?.course_id) return;
    void db.courses.holes(round.course_id).then((h) => {
      setHoles([...h].sort((a, b) => a.hole_num - b.hole_num));
    });
  }, [round?.course_id, prefetchedHoles]);

  useEffect(() => {
    if (!round) return;
    const matrix = scoresMatrixFromDb(
      players,
      (round.scores || []).map((s) => ({
        player_key: s.player_key,
        hole_num: s.hole_num,
        strokes: s.strokes,
      })),
    );
    const scopedScores: Record<string, (number | null)[]> = {};
    players.forEach((p) => {
      const full = matrix[p.player_key] || Array(18).fill(null);
      scopedScores[p.player_key] = full.slice(holeOffset, holeOffset + holeCount);
    });
    setScoresByKey(scopedScores);

    const puttsMatrix: Record<string, (number | null)[]> = {};
    players.forEach((p) => {
      puttsMatrix[p.player_key] = Array(holeCount).fill(null);
    });
    (round.scores || []).forEach((s) => {
      if (!puttsMatrix[s.player_key]) puttsMatrix[s.player_key] = Array(holeCount).fill(null);
      const idx = s.hole_num - 1 - holeOffset;
      if (idx >= 0 && idx < holeCount && s.putts != null) puttsMatrix[s.player_key][idx] = s.putts;
    });
    setPuttsByKey(puttsMatrix);

    const teamConfig = round.config?.teams as string[][] | undefined;
    setTeams(teamConfig);
  }, [round?.id, round?.scores, players.length, holeOffset, holeCount]);

  const recompute = useCallback(() => {
    if (!round || !holes.length) return;
    const roundPlayers = buildRoundPlayers(players, member.id);
    const t = teams;

    if (enabledGames.length === 0) {
      const ctx = { holes, players: roundPlayers, scores: scoresByKey, stakes: {} as StakesConfig, teams: t };
      const strokeResult = strokePlugin.compute(ctx, {});
      setGameResults([strokeResult]);
      setNetByPlayer({});
      setPayments([]);
      return;
    }

    const results = computeAllGames(holes, roundPlayers, scoresByKey, enabledGames, t, puttsByKey);
    const net = aggregateNetOwed(results);
    setGameResults(results);
    setNetByPlayer(net);
    setPayments(minimizePayments(net));
  }, [round, holes, players, member.id, scoresByKey, puttsByKey, enabledGames, teams]);

  useEffect(() => {
    recompute();
  }, [recompute]);

  const setScore = useCallback(async (playerKey: string, holeIndex: number, strokes: number | null) => {
    if (!round) return;
    const holeNum = holeOffset + holeIndex + 1;
    const currentPutts = puttsByKey[playerKey]?.[holeIndex] ?? undefined;
    setScoresByKey((prev) => {
      const arr = [...(prev[playerKey] || Array(holeCount).fill(null))];
      arr[holeIndex] = strokes;
      return { ...prev, [playerKey]: arr };
    });
    await db.groupRounds.upsertScore(round.id, playerKey, holeNum, strokes, currentPutts);
  }, [round, puttsByKey, holeOffset, holeCount]);

  const setPutt = useCallback(async (playerKey: string, holeIndex: number, putts: number | null) => {
    if (!round) return;
    const holeNum = holeOffset + holeIndex + 1;
    const strokes = scoresByKey[playerKey]?.[holeIndex] ?? null;
    setPuttsByKey((prev) => {
      const arr = [...(prev[playerKey] || Array(holeCount).fill(null))];
      arr[holeIndex] = putts;
      return { ...prev, [playerKey]: arr };
    });
    await db.groupRounds.upsertScore(round.id, playerKey, holeNum, strokes, putts);
  }, [round, scoresByKey, holeOffset, holeCount]);

  const playerName = useCallback((key: string) => {
    return players.find((p) => p.player_key === key)?.display_name || key;
  }, [players]);

  const balanceLines = useMemo(() => {
    if (isCasual) return [];
    return Object.entries(netByPlayer)
      .filter(([, v]) => v !== 0)
      .map(([key, cents]) => ({
        playerKey: key,
        name: playerName(key),
        cents,
        you: players.find((p) => p.player_key === key)?.member_id === member.id,
      }))
      .sort((a, b) => b.cents - a.cents);
  }, [netByPlayer, playerName, players, member.id, isCasual]);

  return {
    holes,
    scoresByKey,
    puttsByKey,
    gameResults,
    netByPlayer,
    payments,
    balanceLines,
    setScore,
    setPutt,
    playerName,
    teams,
    isCasual,
  };
}

export function stakesToGameRows(
  enabledIds: string[],
  stakes: StakesConfig,
  players: Array<{ player_key: string; member_id: string | null }>,
  organizerKey: string,
  wolfConfig?: WolfRoundConfig,
  opponentKeys?: Record<string, string>,
  nassauMatches?: Record<string, NassauMatchConfig[]>,
  teams?: string[][],
): Array<{ format_id: string; enabled: boolean; stakes: Record<string, unknown>; config: Record<string, unknown> }> {
  const others = players.filter((p) => p.player_key !== organizerKey);
  const defaultOpp = others[0]?.player_key;
  const allKeys = players.map((p) => p.player_key);

  return enabledIds.map((format_id) => {
    const stakeConfig = stakes[format_id] ?? defaultStakeForFormat(format_id);
    const stakesRow = stakeToDbRow(stakeConfig);
    const config: Record<string, unknown> = {};

    if (format_id === 'nassau' || format_id === 'team-nassau') {
      const legs = getLegStakes(stakeConfig);
      const matches = nassauMatches?.[format_id];
      config.matches = matches?.length
        ? matches
        : defaultNassauMatches(format_id, allKeys, teams, legs);
    }
    if (format_id === 'match-play') {
      const picked = opponentKeys?.[format_id];
      config.opponentKey = picked && picked !== organizerKey ? picked : defaultOpp;
    }
    if (format_id === 'skins' || format_id === 'stableford' || format_id === 'sixes' || format_id === 'putts-bet') {
      config.playerKeys = allKeys;
    }
    if (format_id === 'wolf' || format_id === 'lone-wolf') {
      config.playerKeys = allKeys;
      if (wolfConfig) config.wolfConfig = wolfConfig;
    }
    if (format_id === 'lone-wolf') {
      config.wolfMode = 'lone-only';
    }
    const entry = findCatalogEntry(format_id);
    if (entry?.pluginId === 'wolf' && format_id === 'lone-wolf') {
      config.wolfMode = 'lone-only';
    }

    return { format_id, enabled: true, stakes: stakesRow, config };
  });
}

export function autoTeams(playerKeys: string[]): string[][] {
  if (playerKeys.length >= 4) {
    return [[playerKeys[0], playerKeys[1]], [playerKeys[2], playerKeys[3]]];
  }
  if (playerKeys.length === 3) {
    return [[playerKeys[0], playerKeys[1]], [playerKeys[2]]];
  }
  if (playerKeys.length === 2) {
    return [[playerKeys[0]], [playerKeys[1]]];
  }
  return [[playerKeys[0]], [playerKeys[0]]];
}

export function enabledGamesNeedTeams(enabledIds: string[]): boolean {
  return catalogRequiresTeams(enabledIds);
}
